// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    linkListGridItem?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    linkListGridItem: {
        css: css`
            width: 20%;
            [class*="StyledButton"],
            .section-title {
                text-transform: capitalize;
            }

            .section-title {
                margin-bottom: 6px;
                display: block;

                &,
                > span {
                    font-size: 17px;
                    font-weight: 700;
                    letter-spacing: 0.2px;
                }
            }

            &&:not(:last-child) {
                margin-right: 65px;

                @media (width: 1024px) {
                    margin-right: 25px;
                }
            }

            @media (max-width: 1023px) and (min-width: 769px) {
                width: calc(100% / 3 - 25px);
                margin-bottom: 20px;
                &&:not(:last-child) {
                    margin-right: 25px;
                }
            }

            &&& {
                @media (max-width: 768px) {
                    width: 100%;
                    margin-right: 0;

                    &&:not(:first-child) {
                        margin-top: 5px;
                    }
                }
            }
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    return (
        <StyledWrapper
            css={css`
                display: flex;
                padding-top: 30px;
                padding-bottom: 15px;
                @media (max-width: 1023px) {
                    flex-wrap: wrap;
                }
            `}
        >
            <StyledWrapper {...styles.linkListGridItem}>
                <Zone zoneName="LinkList1" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkListGridItem}>
                <Zone zoneName="LinkList2" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkListGridItem}>
                <Zone zoneName="LinkList3" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkListGridItem}>
                <Zone zoneName="LinkList4" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.linkListGridItem}>
                <Zone zoneName="LinkList5" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
